import React from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Typography,
  Chip,
  alpha,
  Card,
  useTheme,
} from "@mui/material";
import { KanbanCard } from "./KanbanCard";

interface KanbanColumnProps {
  id: string;
  title: string;
  tasks: any[];
  onEdit: (id: string) => void;
}

const getColumnConfig = (id: string) => {
  const theme = useTheme();
  switch (id) {
    case "not_started":
      return {
        bg: theme.palette.action.active,
        color: theme.palette.grey[700],
      }; // Grey
    case "in_progress":
      return {
        bg: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }; // Orange
    case "on_hold":
      return {
        bg: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      }; // Red
    case "completed":
      return {
        bg: theme.palette.success.main,
        color: theme.palette.success.contrastText,
      }; // Green
    default:
      return { bg: theme.palette.grey[200], color: theme.palette.text.primary };
  }
};

const statusColorMap = {
  not_started: "default",
  in_progress: "primary",
  on_hold: "error",
  completed: "success",
};

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  id,
  title,
  tasks,
  onEdit,
}) => {
  const { setNodeRef } = useDroppable({ id });
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 280,
        borderColor: "transparent",
        height: "100%",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 1.5,
          backgroundColor: alpha(getColumnConfig(id).bg, 0.1),
        }}
      >
        <Chip
          label={title}
          size="small"
          color={statusColorMap[id as keyof typeof statusColorMap] as any}
        />
        <Typography variant="body1" color="text.secondary" fontWeight="500">
          {tasks.length}
        </Typography>
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 100,
          // Hide scrollbar but keep functionality
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: "3px",
          },
          backgroundColor: theme.palette.action.hover,
          height: "100%",
          px: 2,
          pt: 2,
          pb: 6,
        }}
      >
        <SortableContext
          items={tasks.map((t) => t.task_id)}
          strategy={verticalListSortingStrategy}
        >
          <Box display="flex" flexDirection="column" gap={0}>
            {tasks.map((task) => (
              <KanbanCard
                key={task.task_id}
                id={task.task_id}
                task={task}
                onClick={onEdit}
              />
            ))}
          </Box>
        </SortableContext>
      </Box>
    </Card>
  );
};
